import { useState } from 'react'

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: '',
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle')
  const [privacyAccepted, setPrivacyAccepted] = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)
  const [agreeError, setAgreeError] = useState('')

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
  }

  const openConfirm = (e: React.FormEvent) => {
    e.preventDefault()
    setSubmitStatus('idle')
    setAgreeError('')
    if (!privacyAccepted) {
      setAgreeError('送信にはプライバシーポリシーへの同意が必要です。')
      return
    }
    setShowConfirm(true)
  }

  const actuallySubmit = async () => {
    try {
      setIsSubmitting(true)
      const ok = await (window as any).phpSubmit?.(formData)
      if (ok) {
        setSubmitStatus('success')
        setFormData({ name: '', email: '', phone: '', service: '', message: '' })
        setPrivacyAccepted(false)
      } else {
        setSubmitStatus('error')
      }
    } catch {
      setSubmitStatus('error')
    } finally {
      setIsSubmitting(false)
      setShowConfirm(false)
    }
  }

  return (
    <section id="contact" className="py-20 bg-gradient-to-br from-pink-50 to-teal-50">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h3 className="text-4xl md:text-5xl font-bold mb-6 text-gray-800">
            <strong>お問い合わせ</strong>
          </h3>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            ネイルレッスンや商品に関するご質問、ご相談はお気軽にお問い合わせください
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Left: Form */}
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <h4 className="text-2xl font-bold mb-6 text-gray-800">お問い合わせフォーム</h4>

            <form onSubmit={openConfirm} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
                    お名前 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent text-sm"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                    メールアドレス <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent text-sm"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">
                    電話番号
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent text-sm"
                  />
                </div>
                <div>
                  <label htmlFor="service" className="block text-sm font-semibold text-gray-700 mb-2">
                    ご希望サービス
                  </label>
                  <select
                    id="service"
                    name="service"
                    value={formData.service}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent text-sm"
                  >
                    <option value="">選択してください</option>
                    <option value="ネイルレッスン">ネイルレッスン</option>
                    <option value="オンラインレッスン">オンラインレッスン</option>
                    <option value="商品購入">商品購入</option>
                    <option value="その他">その他</option>
                  </select>
                </div>
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">
                  お問い合わせ内容 <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  required
                  rows={5}
                  maxLength={500}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent text-sm resize-vertical"
                ></textarea>
                <p className="text-xs text-gray-500 mt-1">{formData.message.length}/500文字</p>
              </div>

              {/* プライバシーポリシー同意 */}
              <div className="flex items-start gap-3">
                <input
                  id="agree"
                  type="checkbox"
                  checked={privacyAccepted}
                  onChange={(e) => setPrivacyAccepted(e.target.checked)}
                  className="mt-1 h-5 w-5 rounded border-gray-300 text-pink-600 focus:ring-pink-500 cursor-pointer"
                  required
                />
                <label htmlFor="agree" className="text-sm text-gray-700">
                  <a href="/privacy" className="text-pink-600 underline"  rel="noreferrer">
                    プライバシーポリシー
                  </a>
                  に同意します
                </label>
              </div>
              {agreeError && <p className="text-sm text-red-600">{agreeError}</p>}

              <button
                type="submit"
                disabled={isSubmitting || !privacyAccepted}
                className="w-full bg-gradient-to-r from-pink-500 to-teal-500 text-white py-4 rounded-lg font-semibold hover:shadow-lg transition-all disabled:opacity-50"
              >
                送信する
              </button>

              {submitStatus === 'success' && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-green-700">
                  お問い合わせありがとうございます。後日担当者よりご連絡いたします。
                </div>
              )}
              {submitStatus === 'error' && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
                  送信に失敗しました。しばらく時間をおいて再度お試しください。
                </div>
              )}
            </form>
          </div>

          {/* ✅ ここからは既存の Contact Information を削除せず残す */}
          <div className="space-y-8">
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <h4 className="text-2xl font-bold mb-6 text-gray-800">アクセス情報</h4>
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-gradient-to-r from-pink-400 to-pink-600 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-map-pin-line text-white text-xl"></i>
                  </div>
                  <div>
                    <h5 className="font-semibold text-gray-800 mb-2">所在地</h5>
                    <p className="text-gray-600">
                      〒546-0043<br />
                      大阪府大阪市東住吉区駒川3-27-39<br />
                      プレジオールKOMAGAWA２０４
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-gradient-to-r from-teal-400 to-teal-600 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-time-line text-white text-xl"></i>
                  </div>
                  <div>
                    <h5 className="font-semibold text-gray-800 mb-2">営業時間</h5>
                    <p className="text-gray-600">
                      平日：10:00 - 19:00<br />
                      土日：10:00 - 17:00<br />
                      定休日：月曜日
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-gradient-to-r from-purple-400 to-purple-600 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-phone-line text-white text-xl"></i>
                  </div>
                  <div>
                    <h5 className="font-semibold text-gray-800 mb-2">お電話でのお問い合わせ</h5>
                    <p className="text-gray-600">
                      受付時間：営業時間内<br />
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Map */}
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3284.123456789!2d135.5023!3d34.6198!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMzTCsDM3JzExLjMiTiAxMzXCsDMwJzA4LjMiRQ!5e0!3m2!1sja!2sjp!4v1234567890123"
                width="100%"
                height="300"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="株式会社Ｍｓ　Ｐｌａｎｔａｇｅ 所在地"
              ></iframe>
            </div>
          </div>
        </div>
      </div>

      {/* 確認モーダル */}
      {showConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => setShowConfirm(false)} />
          <div className="relative bg-white rounded-2xl shadow-2xl p-6 w-full max-w-lg">
            <h5 className="text-xl font-bold mb-4">送信内容の確認</h5>
            <div className="space-y-2 text-sm text-gray-700">
              <p><span className="font-semibold">お名前：</span>{formData.name || '-'}</p>
              <p><span className="font-semibold">メール：</span>{formData.email || '-'}</p>
              <p><span className="font-semibold">電話番号：</span>{formData.phone || '-'}</p>
              <p><span className="font-semibold">サービス：</span>{formData.service || '-'}</p>
              <p className="whitespace-pre-wrap">
                <span className="font-semibold">お問い合わせ内容：</span>
                {formData.message || '-'}
              </p>
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setShowConfirm(false)}
                className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
              >
                修正する
              </button>
              <button
                type="button"
                onClick={actuallySubmit}
                disabled={isSubmitting}
                className="px-4 py-2 rounded-lg bg-gradient-to-r from-pink-500 to-teal-500 text-white hover:opacity-90 disabled:opacity-50"
              >
                {isSubmitting ? '送信中…' : 'この内容で送信する'}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  )
}
